import React from 'react';
import { Box, Grid, InputAdornment } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { LockOutlined as LockIcon } from '@mui/icons-material';
import { DecimalNumberInput, SegmentedControl } from '../ui';
import { APP_CURRENCY } from '../../utils/constants/currency';
import { formatCurrency } from '../../utils/formatters/money';
import { FormField, FormLabel, HelperText } from './ProductDrawer/ProductDrawer.styles';
import { CostEntryMode, derivePotentialEconomics } from './productCostPricing.utils';

const COST_ENTRY_OPTIONS = [
  { value: 'PER_PIECE' as const, label: 'Por pieza', ariaLabel: 'costo por pieza' },
  { value: 'PER_GRAM' as const, label: 'Por gramo', ariaLabel: 'costo por gramo' },
];

/**
 * Campo de costo bloqueado (calculado o sin peso): fondo gris y texto legible, para que se
 * distinga a simple vista del campo editable (borde verde del tema) sin parecer vacío.
 */
const lockedFieldSx: SxProps<Theme> = (theme) => ({
  '& .MuiOutlinedInput-root.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.06)
        : theme.palette.grey[100],
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: theme.palette.text.secondary,
    WebkitTextFillColor: theme.palette.text.secondary,
    cursor: 'not-allowed',
  },
});

const currencyAdornment = (locked: boolean) => (
  <InputAdornment position="start" sx={{ gap: 0.75 }}>
    {locked ? <LockIcon sx={{ fontSize: 16, color: 'text.disabled' }} /> : null}
    {APP_CURRENCY.symbol}
  </InputAdornment>
);

const LockedTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
    {' · '}
    {children}
  </Box>
);

export interface ProductCostEntryFieldsProps {
  costEntryMode: CostEntryMode;
  costPerGram: string;
  costPrice: string;
  sellingPrice: string;
  hasWeight: boolean;
  onCostEntryModeChange: (next: CostEntryMode) => void;
  onCostPriceChange: (val: number | null) => void;
  onCostPerGramChange: (val: number | null) => void;
  onSellingPriceChange: (val: number | null) => void;
}

export const ProductCostEntryFields: React.FC<ProductCostEntryFieldsProps> = ({
  costEntryMode,
  costPerGram,
  costPrice,
  sellingPrice,
  hasWeight,
  onCostEntryModeChange,
  onCostPriceChange,
  onCostPerGramChange,
  onSellingPriceChange,
}) => {
  const economics = derivePotentialEconomics(costPrice, sellingPrice);
  const pieceDisabled = costEntryMode === 'PER_GRAM';
  const gramDisabled = costEntryMode === 'PER_PIECE' || !hasWeight;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Cómo cargar el costo</FormLabel>
          <SegmentedControl
            value={costEntryMode}
            options={COST_ENTRY_OPTIONS}
            onChange={onCostEntryModeChange}
            ariaLabel="modo de costo"
            fullWidth
            size="small"
          />
          <HelperText>Al cambiar de opción se reinician los costos para cargar de cero.</HelperText>
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>
            Costo por pieza{costEntryMode === 'PER_PIECE' ? ' *' : ''}
            {pieceDisabled ? <LockedTag>Calculado</LockedTag> : null}
          </FormLabel>
          <DecimalNumberInput
            fullWidth
            name="costPrice"
            value={costPrice}
            onChange={onCostPriceChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            disabled={pieceDisabled}
            sx={pieceDisabled ? lockedFieldSx : undefined}
            InputProps={{ startAdornment: currencyAdornment(pieceDisabled) }}
            required={costEntryMode === 'PER_PIECE'}
          />
          {costEntryMode === 'PER_GRAM' ? (
            <HelperText>Calculado automáticamente: peso × costo por gramo</HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>
            Costo por gramo{costEntryMode === 'PER_GRAM' ? ' *' : ''}
            {gramDisabled ? (
              <LockedTag>{hasWeight ? 'Calculado' : 'No disponible'}</LockedTag>
            ) : null}
          </FormLabel>
          <DecimalNumberInput
            fullWidth
            name="costPerGram"
            value={costPerGram}
            onChange={onCostPerGramChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            disabled={gramDisabled}
            sx={gramDisabled ? lockedFieldSx : undefined}
            InputProps={{ startAdornment: currencyAdornment(gramDisabled) }}
            required={costEntryMode === 'PER_GRAM'}
          />
          {costEntryMode === 'PER_PIECE' ? (
            !hasWeight ? (
              <HelperText>Defina el peso en Propiedades para ver el costo por gramo</HelperText>
            ) : null
          ) : !hasWeight ? (
            <HelperText color="warning.main">
              Defina el peso en Propiedades para calcular el costo por pieza
            </HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
      <Grid item xs={12}>
        <FormField>
          <FormLabel>Precio de Venta</FormLabel>
          <DecimalNumberInput
            fullWidth
            name="sellingPrice"
            value={sellingPrice}
            onChange={onSellingPriceChange}
            variant="outlined"
            size="small"
            placeholder="0,00"
            decimalPlaces={2}
            min={0}
            max={999999.99}
            InputProps={{ startAdornment: currencyAdornment(false) }}
          />
          {economics != null ? (
            <HelperText>
              Utilidad potencial: {formatCurrency(economics.profit)} · Margen potencial:{' '}
              {economics.marginPercent}%
            </HelperText>
          ) : (
            <HelperText color="warning.main">Solo se permiten hasta 2 decimales</HelperText>
          )}
        </FormField>
      </Grid>
    </Grid>
  );
};
